/**
 * LiveClassQuestionsPage — pre-class Q&A for a scheduled live class.
 *
 * Attendees post + upvote each other's questions before the cutoff (default 24h
 * before class). Hosts can reply, mark answered, pin, and moderate.
 *
 * URL: /live-classes/:classId/questions (both attendee & host)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  MessagesSquare, ThumbsUp, CheckCircle2, Pin, PinOff,
  Loader2, ArrowLeft, Lock, Calendar, Clock, User as UserIcon,
  Reply, Trash2, Pencil, AlertCircle, Send, Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtIST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

const relativeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} d ago`;
  return fmtIST(iso);
};

const relativeUntil = (iso) => {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'closed';
  const s = Math.floor(diff / 1000);
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} hours`;
  const d = Math.floor(h / 24);
  return `${d} days`;
};

// ---------- Question card ----------
const QuestionCard = ({ q, isHostView, onUpvote, onDelete, onEdit, onReply, onClearReply, onTogglePin, onToggleAnswered, locked, frozen }) => {
  const [showReply, setShowReply] = useState(false);
  const [replyDraft, setReplyDraft] = useState(q.host_reply?.text || '');
  const [savingReply, setSavingReply] = useState(false);

  const submitReply = async () => {
    const t = replyDraft.trim();
    if (!t) { toast.error('Reply cannot be empty'); return; }
    try {
      setSavingReply(true);
      await onReply(q.id, t);
      setShowReply(false);
      toast.success('Reply saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save reply');
    } finally {
      setSavingReply(false);
    }
  };

  return (
    <Card
      className={`transition-all ${q.is_pinned ? 'border-2 border-amber-300 bg-amber-50/40' : ''} ${q.is_answered ? 'border-l-4 border-l-emerald-500' : ''}`}
      data-testid={`qna-question-card-${q.id}`}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Upvote column */}
          <div className="flex flex-col items-center gap-1 min-w-[52px]">
            <button
              onClick={() => onUpvote(q.id)}
              disabled={locked || q.is_self}
              title={q.is_self ? "You can't upvote your own question" : (q.has_upvoted ? 'Remove upvote' : 'Upvote')}
              className={`w-11 h-11 rounded-lg border transition-all flex items-center justify-center ${
                q.has_upvoted
                  ? 'bg-[#0A7EA4] text-white border-[#0A7EA4] hover:bg-[#086684]'
                  : 'bg-white border-gray-300 text-gray-600 hover:border-[#0A7EA4] hover:text-[#0A7EA4]'
              } ${(locked || q.is_self) ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`qna-upvote-btn-${q.id}`}
            >
              <ThumbsUp className="w-4 h-4" fill={q.has_upvoted ? 'currentColor' : 'none'} />
            </button>
            <span className="text-sm font-semibold tabular-nums text-gray-700" data-testid={`qna-upvote-count-${q.id}`}>
              {q.upvote_count}
            </span>
          </div>

          {/* Question body */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {q.is_pinned && (
                <Badge className="bg-amber-500 text-white border-0 gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </Badge>
              )}
              {q.is_answered && (
                <Badge className="bg-emerald-600 text-white border-0 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Answered
                </Badge>
              )}
              {q.is_anonymous && !isHostView && (
                <Badge variant="outline" className="text-xs text-gray-600">Anonymous</Badge>
              )}
              {q.is_anonymous && isHostView && (
                <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50">
                  Posted as Anonymous
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-900 whitespace-pre-wrap" data-testid={`qna-question-text-${q.id}`}>{q.text}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <UserIcon className="w-3 h-3" />
              <span>{q.author_name}</span>
              <span>·</span>
              <span>{relativeAgo(q.created_at)}</span>
              {q.updated_at && q.updated_at !== q.created_at && (
                <span className="italic">(edited)</span>
              )}
            </div>

            {/* Host reply block */}
            {q.host_reply && (
              <div className="mt-3 pl-3 border-l-2 border-[#0A7EA4] bg-[#0A7EA4]/5 py-2 pr-3 rounded-r">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#0A7EA4] mb-0.5">
                  <Reply className="w-3 h-3" />
                  <span>{q.host_reply.replied_by_name} · Faculty</span>
                  <span className="text-gray-400 font-normal">· {relativeAgo(q.host_reply.replied_at)}</span>
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap" data-testid={`qna-reply-text-${q.id}`}>
                  {q.host_reply.text}
                </p>
              </div>
            )}

            {/* Reply editor */}
            {showReply && (
              <div className="mt-3 border rounded-lg p-3 bg-gray-50" data-testid={`qna-reply-editor-${q.id}`}>
                <Textarea
                  value={replyDraft}
                  onChange={(e) => setReplyDraft(e.target.value)}
                  placeholder="Write a reply that attendees will see next to their question…"
                  className="min-h-[80px] mb-2 bg-white"
                  data-testid={`qna-reply-textarea-${q.id}`}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setShowReply(false)}>Cancel</Button>
                  <Button size="sm" onClick={submitReply} disabled={savingReply}
                    className="bg-[#0A7EA4] hover:bg-[#086684]" data-testid={`qna-reply-save-btn-${q.id}`}>
                    {savingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <><Send className="w-4 h-4 mr-1" /> Save reply</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Action bar */}
            <div className="flex items-center gap-1 flex-wrap mt-2">
              {isHostView && !showReply && !frozen && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => { setReplyDraft(q.host_reply?.text || ''); setShowReply(true); }}
                    className="h-7 text-xs text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                    data-testid={`qna-reply-btn-${q.id}`}>
                    <Reply className="w-3 h-3 mr-1" />
                    {q.host_reply ? 'Edit reply' : 'Reply'}
                  </Button>
                  {q.host_reply && (
                    <Button size="sm" variant="ghost" onClick={() => onClearReply(q.id)}
                      className="h-7 text-xs text-gray-500 hover:text-red-600"
                      data-testid={`qna-clear-reply-btn-${q.id}`}>
                      Clear reply
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onToggleAnswered(q.id)}
                    className={`h-7 text-xs ${q.is_answered ? 'text-emerald-700' : 'text-gray-600'} hover:bg-emerald-50`}
                    data-testid={`qna-toggle-answered-btn-${q.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {q.is_answered ? 'Mark unanswered' : 'Mark answered'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onTogglePin(q.id)}
                    className={`h-7 text-xs ${q.is_pinned ? 'text-amber-700' : 'text-gray-600'} hover:bg-amber-50`}
                    data-testid={`qna-toggle-pin-btn-${q.id}`}>
                    {q.is_pinned ? <PinOff className="w-3 h-3 mr-1" /> : <Pin className="w-3 h-3 mr-1" />}
                    {q.is_pinned ? 'Unpin' : 'Pin'}
                  </Button>
                </>
              )}
              {q.can_edit && !frozen && (
                <Button size="sm" variant="ghost" onClick={() => onEdit(q)}
                  className="h-7 text-xs text-gray-600" data-testid={`qna-edit-btn-${q.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
              )}
              {q.can_delete && !frozen && (
                <Button size="sm" variant="ghost" onClick={() => onDelete(q.id)}
                  className="h-7 text-xs text-red-600 hover:bg-red-50" data-testid={`qna-delete-btn-${q.id}`}>
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- Main page ----------
const LiveClassQuestionsPage = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();

  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [postText, setPostText] = useState('');
  const [postAnon, setPostAnon] = useState(false);
  const [posting, setPosting] = useState(false);
  const [sortMode, setSortMode] = useState('top'); // 'top' | 'new'
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'answered' | 'unanswered' | 'mine'

  const [editing, setEditing] = useState(null); // { id, text }
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setState(s => ({ ...s, loading: true, error: '' }));
      const { data } = await axios.get(`${API}/live-classes/${classId}/questions`);
      setState({ loading: false, error: '', data });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Failed to load Q&A');
      if (err.response?.status === 403) {
        setState({ loading: false, error: 'Live Classes are not included in your current plan. Upgrade to unlock Q&A.', data: null });
      } else {
        setState({ loading: false, error: msg, data: null });
      }
    }
  }, [classId]);

  useEffect(() => { load(); }, [load]);
  // Silent refresh every 30s so upvotes/replies show up without a manual reload
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const cls = state.data?.class;
  const isLocked = !!state.data?.is_locked;
  const isHostView = !!state.data?.is_host_view;
  const isFrozen = !!state.data?.is_frozen;
  const qnaReadyAt = state.data?.qna_ready_at || null;
  const qnaReadyBy = state.data?.qna_ready_by_name || null;
  const publishedAt = state.data?.published_at || null;
  const questions = state.data?.questions || [];

  const [markingReady, setMarkingReady] = useState(false);
  const toggleReady = async () => {
    try {
      setMarkingReady(true);
      const { data } = await axios.post(`${API}/live-classes/${classId}/questions/ready`);
      toast.success(data.ready ? 'Marked ready for archive' : 'Unmarked — still curating');
      await load(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to update flag'));
    } finally {
      setMarkingReady(false);
    }
  };

  const submitPost = async () => {
    const t = postText.trim();
    if (t.length < 3) { toast.error('Question must be at least 3 characters'); return; }
    try {
      setPosting(true);
      await axios.post(`${API}/live-classes/${classId}/questions`, { text: t, is_anonymous: postAnon });
      setPostText('');
      setPostAnon(false);
      toast.success('Question posted');
      await load(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const onUpvote = async (qid) => {
    try {
      await axios.post(`${API}/live-classes/${classId}/questions/${qid}/upvote`);
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upvote'); }
  };

  const onDelete = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/live-classes/${classId}/questions/${qid}`);
      toast.success('Question deleted');
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const onReply = async (qid, text) => {
    await axios.post(`${API}/live-classes/${classId}/questions/${qid}/reply`, { text });
    await load(true);
  };

  const onClearReply = async (qid) => {
    if (!window.confirm('Remove your reply? This will also mark the question as unanswered.')) return;
    try {
      await axios.delete(`${API}/live-classes/${classId}/questions/${qid}/reply`);
      toast.success('Reply removed');
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear reply'); }
  };

  const onTogglePin = async (qid) => {
    try {
      await axios.post(`${API}/live-classes/${classId}/questions/${qid}/pin`);
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle pin'); }
  };

  const onToggleAnswered = async (qid) => {
    try {
      await axios.post(`${API}/live-classes/${classId}/questions/${qid}/answered`);
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle answered'); }
  };

  const openEdit = (q) => { setEditing(q); setEditDraft(q.text); };
  const closeEdit = () => { setEditing(null); setEditDraft(''); };
  const saveEdit = async () => {
    const t = editDraft.trim();
    if (t.length < 3) { toast.error('Question is too short'); return; }
    try {
      setSavingEdit(true);
      await axios.patch(`${API}/live-classes/${classId}/questions/${editing.id}`, { text: t });
      toast.success('Question updated');
      closeEdit();
      await load(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSavingEdit(false); }
  };

  // Filtered + sorted list
  const visible = useMemo(() => {
    let list = [...questions];
    if (filterMode === 'answered') list = list.filter(q => q.is_answered);
    else if (filterMode === 'unanswered') list = list.filter(q => !q.is_answered);
    else if (filterMode === 'mine') list = list.filter(q => q.is_self);
    if (sortMode === 'new') {
      // Pinned still floats to the top
      list.sort((a, b) => (Number(b.is_pinned) - Number(a.is_pinned)) || (new Date(b.created_at) - new Date(a.created_at)));
    }
    // 'top' is the backend default order (pinned → upvotes → newest)
    return list;
  }, [questions, filterMode, sortMode]);

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto" data-testid="live-class-questions-page">
        <Button variant="ghost" size="sm" onClick={() => {
          // Prefer the explicit origin passed by the caller (via location.state.from).
          // Fall back to role-based routing for direct URL visits: admins go to the
          // admin dashboard, everyone else (including candidates who can't reach
          // /admin/*) goes to the user Live Classes list.
          const backPath = location.state?.from
            || (isHostView ? '/admin/live-classes' : '/live-classes');
          navigate(backPath);
        }}
          className="mb-4 text-gray-600 hover:text-gray-900" data-testid="qna-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Live Classes
        </Button>

        {state.loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
        ) : state.error ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-900 mb-1">Unable to load Q&amp;A</p>
                <p className="text-sm text-amber-800">{state.error}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Header card */}
            <Card className="mb-5 border-l-4 border-l-[#0A7EA4] shadow-sm">
              <CardHeader className="bg-gradient-to-r from-[#0A7EA4]/5 to-transparent">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <MessagesSquare className="w-5 h-5 text-[#0A7EA4]" />
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#0A7EA4]">Pre-class Q&amp;A</span>
                    </div>
                    <CardTitle className="text-2xl text-gray-900" data-testid="qna-class-title">{cls?.title}</CardTitle>
                    <CardDescription className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtIST(cls?.start_time)}</span>
                      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {cls?.duration_minutes} min</span>
                      <span className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" /> {cls?.host_user_name}</span>
                      {Array.isArray(cls?.grades) && cls.grades.map(g => (
                        <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                      ))}
                    </CardDescription>
                  </div>

                  {/* Cutoff pill */}
                  <div className="text-right">
                    {isLocked ? (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-gray-700 text-xs font-medium">
                        <Lock className="w-3.5 h-3.5" />
                        Q&amp;A closed
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
                        <Sparkles className="w-3.5 h-3.5" />
                        Open · closes in {relativeUntil(cls?.qna_closes_at)}
                      </div>
                    )}
                    <p className="text-[10px] text-gray-500 mt-1">
                      Closes {fmtIST(cls?.qna_closes_at)}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-4">
                {/* Composer */}
                {isFrozen ? (
                  <div
                    className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm"
                    data-testid="qna-frozen-banner"
                  >
                    <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-1">
                      <Lock className="w-4 h-4" />
                      Archive published — questions are frozen
                    </div>
                    <p className="text-xs text-emerald-900/80">
                      This class&apos;s archive is live for students. The Q&amp;A shown here is now permanent —
                      no edits, deletes, or replies can be made.
                      {isHostView && (
                        <> If something needs fixing, ask an admin to <strong>unpublish</strong> the archive first.</>
                      )}
                    </p>
                  </div>
                ) : isLocked ? (
                  isHostView ? (
                    <div className="rounded-lg border border-[#0A7EA4]/30 bg-[#0A7EA4]/5 p-4 text-sm" data-testid="qna-curator-banner">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                        <div className="flex items-center gap-2 font-semibold text-[#0A7EA4]">
                          <Sparkles className="w-4 h-4" />
                          Curator mode
                        </div>
                        {qnaReadyAt ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 gap-1" data-testid="qna-ready-badge">
                              <CheckCircle2 className="w-3 h-3" />
                              Marked ready {qnaReadyBy ? `by ${qnaReadyBy}` : ''}
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={toggleReady}
                              disabled={markingReady || !!publishedAt}
                              className="h-7 text-xs"
                              data-testid="qna-unmark-ready-btn"
                            >
                              {markingReady ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Unmark'}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            onClick={toggleReady}
                            disabled={markingReady}
                            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="qna-mark-ready-btn"
                          >
                            {markingReady ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                            Mark ready for archive
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-gray-700">
                        Attendees can no longer post or vote here. You can still <strong>edit, delete, pin, mark answered, and reply</strong> to
                        clean up this list before publishing. Whatever remains here will appear on the class archive.
                        {!qnaReadyAt && <> When you&apos;re done curating, hit <strong>Mark ready</strong> so the admin knows to publish.</>}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2 font-semibold text-gray-800 mb-1">
                        <Lock className="w-4 h-4" />
                        {state.data.lock_reason || 'Q&A window has closed'}
                      </div>
                      <p className="text-xs text-gray-500">
                        No new questions or votes can be added.
                        {state.data.total > 0 && ' You can still read the archive below and any faculty replies.'}
                      </p>
                    </div>
                  )
                ) : (
                  <div className="rounded-lg border border-gray-200 p-3" data-testid="qna-composer">
                    <Textarea
                      value={postText}
                      onChange={(e) => setPostText(e.target.value)}
                      placeholder="Ask a specific question about the topic. The class host will see the top upvoted questions before your class."
                      className="min-h-[80px] mb-2 resize-none"
                      maxLength={1000}
                      data-testid="qna-composer-textarea"
                    />
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Switch id="qna-anon" checked={postAnon} onCheckedChange={setPostAnon}
                          data-testid="qna-composer-anon-switch" />
                        <Label htmlFor="qna-anon" className="text-xs text-gray-600 cursor-pointer">
                          Ask anonymously <span className="text-gray-400">(host still sees your name)</span>
                        </Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-gray-400 tabular-nums">{postText.length}/1000</span>
                        <Button
                          onClick={submitPost}
                          disabled={posting || postText.trim().length < 3}
                          size="sm"
                          className="bg-[#0A7EA4] hover:bg-[#086684]"
                          data-testid="qna-composer-submit"
                        >
                          {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Post question</>}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats + controls */}
                <div className="flex items-center justify-between flex-wrap gap-3 mt-4 text-sm">
                  <div className="text-gray-600" data-testid="qna-stats">
                    <span className="font-semibold text-gray-900">{state.data.total}</span> {state.data.total === 1 ? 'question' : 'questions'}
                    {' · '}
                    <span className="font-semibold text-emerald-700">{state.data.answered_count}</span> answered
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {['all', 'unanswered', 'answered', 'mine'].map(m => (
                      <Button key={m} size="sm" variant={filterMode === m ? 'default' : 'ghost'}
                        onClick={() => setFilterMode(m)} className={`h-7 text-xs ${filterMode === m ? 'bg-[#0A7EA4] hover:bg-[#086684]' : ''}`}
                        data-testid={`qna-filter-${m}`}>
                        {m[0].toUpperCase() + m.slice(1)}
                      </Button>
                    ))}
                    <div className="w-px h-4 bg-gray-300 mx-1" />
                    {['top', 'new'].map(m => (
                      <Button key={m} size="sm" variant={sortMode === m ? 'default' : 'ghost'}
                        onClick={() => setSortMode(m)} className={`h-7 text-xs ${sortMode === m ? 'bg-[#0A7EA4] hover:bg-[#086684]' : ''}`}
                        data-testid={`qna-sort-${m}`}>
                        {m === 'top' ? 'Top voted' : 'Newest'}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Question list */}
            {visible.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-gray-500" data-testid="qna-empty-state">
                  <MessagesSquare className="w-14 h-14 mx-auto mb-3 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-1">
                    {questions.length === 0 ? 'No questions yet' : 'No questions match this filter'}
                  </h3>
                  <p className="text-sm">
                    {questions.length === 0
                      ? 'Be the first to ask — your peers can upvote and the host will see the top questions before class.'
                      : 'Try a different filter.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="qna-list">
                {visible.map(q => (
                  <QuestionCard
                    key={q.id}
                    q={q}
                    isHostView={isHostView}
                    locked={isLocked}
                    frozen={isFrozen}
                    onUpvote={onUpvote}
                    onDelete={onDelete}
                    onEdit={openEdit}
                    onReply={onReply}
                    onClearReply={onClearReply}
                    onTogglePin={onTogglePin}
                    onToggleAnswered={onToggleAnswered}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) closeEdit(); }}>
        <DialogContent data-testid="qna-edit-dialog">
          <DialogHeader>
            <DialogTitle>{isHostView ? 'Edit question (curator)' : 'Edit your question'}</DialogTitle>
            <DialogDescription className="text-xs">
              {isHostView
                ? 'You are editing as the class host. Only fix typos or trim wording — leave the meaning intact so upvotes still make sense.'
                : 'You can edit within 15 minutes of posting. After that, the question is locked to protect upvote signal.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={editDraft}
            onChange={(e) => setEditDraft(e.target.value)}
            className="min-h-[100px]"
            maxLength={1000}
            data-testid="qna-edit-textarea"
          />
          <div className="text-right text-[10px] text-gray-400 tabular-nums">{editDraft.length}/1000</div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEdit}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-[#0A7EA4] hover:bg-[#086684]" data-testid="qna-edit-save-btn">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default LiveClassQuestionsPage;
